<script setup lang="ts">
    import { computed, onMounted, ref, watch } from 'vue';
    import Loading from '@shared/components/Loading.vue';
    import MultiSelect from '@shared/components/MultiSelect/MultiSelect.vue';
    import FormSelect from '@shared/components/Form/FormSelect.vue';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import BSToggleButtons from '@shared/components/BSToggleButtons.vue';
    import { formatNumber } from '@shared/utils/formatNumber';
    import InsightsWidget from './components/InsightsWidget.vue';
    import InsightsIcon from './components/InsightsIcon.vue';
    import InsightsKpiTile from './components/InsightsKpiTile.vue';
    import InsightsTrendChart from './components/InsightsTrendChart.vue';
    import InsightsActivityChart from './components/InsightsActivityChart.vue';
    import InsightsBarList from './components/InsightsBarList.vue';
    import InsightsRecentIssues from './components/InsightsRecentIssues.vue';
    import InsightsTopSubscribers from './components/InsightsTopSubscribers.vue';
    import InsightsAiSummary from './components/InsightsAiSummary.vue';
    import { getInsightsIssuesReport, getInsightsReportMonths, getInsightsSubscription } from './insights-dummy-data';
    import type { InsightsIssuesReportData, InsightsMonthlyTrend, InsightsReportScope, InsightsSubscription } from './insights-dummy-data';
    import { exportInsightsIssuesReport } from './insights-export';
    import type { InsightsExportFormat } from './insights-export';
    import type { InsightsBarListItem } from './insights-theme';
    import { INSIGHTS_COLORS, INSIGHTS_NEUTRAL, INSIGHTS_PRIMARY, formatCompact, formatMonth, formatMonthYear, formatPercent } from './insights-theme';

    const data = ref<InsightsIssuesReportData | null>(null);
    const subscription = ref<InsightsSubscription | null>(null);
    const loading = ref(true);

    // Reports are per completed calendar month (yyyy-MM), newest first; defaults to the latest
    const months = ref<string[]>([]);
    const month = ref('');
    const monthLabel = computed(() => (month.value ? formatMonthYear(month.value) : ''));
    const monthOptions = computed(() => months.value.map((m) => ({ label: formatMonthYear(m), value: m })));

    // Scope filter: no selection means the whole subscription
    const selectedAccountIds = ref<string[]>([]);

    const scope = computed<InsightsReportScope>(() =>
        selectedAccountIds.value.length ? { Type: 'accounts', AccountIds: selectedAccountIds.value } : { Type: 'subscription' }
    );

    const scopeDescription = computed(() => {
        const accounts = subscription.value?.Accounts ?? [];
        const subscriptionName = subscription.value?.Name ?? 'the subscription';
        if (scope.value.Type === 'subscription') return `Mailings sent in ${monthLabel.value} from all ${accounts.length} accounts in ${subscriptionName}`;
        const accountIds = scope.value.AccountIds;
        const names = accounts.filter((a) => accountIds.includes(a.AccountId)).map((a) => a.Name);
        const filtered = names.length <= 3 ? names.join(', ') : `${names.length} accounts`;
        return `Mailings sent in ${monthLabel.value} from ${filtered} (${names.length} of ${accounts.length} accounts in ${subscriptionName})`;
    });

    // Ignore responses from scopes or months the user has already switched away from
    let requestId = 0;
    async function loadReport() {
        if (!month.value) return;
        const current = ++requestId;
        loading.value = true;
        try {
            const report = await getInsightsIssuesReport(scope.value, month.value);
            if (current === requestId) data.value = report;
        } catch (error) {
            // Don't keep showing the previous scope's or month's report
            if (current === requestId) data.value = null;
            console.error('Failed to load insights report:', error);
        } finally {
            if (current === requestId) loading.value = false;
        }
    }

    watch([scope, month], loadReport, { deep: true });

    onMounted(async () => {
        try {
            [subscription.value, months.value] = await Promise.all([getInsightsSubscription(), getInsightsReportMonths()]);
        } catch (error) {
            console.error('Failed to load insights report months:', error);
        }
        // Setting the month triggers the first load; with no completed month there is nothing to load
        if (months.value.length) month.value = months.value[0];
        else loading.value = false;
    });

    function exportReport(format: InsightsExportFormat) {
        if (data.value) exportInsightsIssuesReport(data.value, month.value, format);
    }

    function print() {
        window.print();
    }

    const trendLabels = computed(() => data.value?.MonthlyTrend.map((m) => formatMonth(m.Month, 'long')) ?? []);

    // Current month vs previous month
    function compare(pick: (m: InsightsMonthlyTrend) => number, unit: '%' | 'pp'): number {
        const trend = data.value?.MonthlyTrend ?? [];
        if (trend.length < 2) return 0;
        const current = pick(trend[trend.length - 1]);
        const previous = pick(trend[trend.length - 2]);
        if (unit === 'pp') return current - previous;
        return previous > 0 ? ((current - previous) / previous) * 100 : 0;
    }

    const kpis = computed(() => {
        const trend = data.value?.MonthlyTrend ?? [];
        const latest = trend[trend.length - 1];
        if (!latest) return [];
        return [
            {
                label: 'Mailings sent',
                value: formatNumber(latest.IssueCount),
                delta: compare((m) => m.IssueCount, '%'),
                deltaUnit: '%' as const,
                higherIsBetter: true,
                trend: trend.map((m) => m.IssueCount),
                color: 'primary' as const,
                format: (v: number) => `${formatNumber(v)} mailings`,
            },
            {
                label: 'Emails sent',
                value: formatNumber(latest.SentCount),
                delta: compare((m) => m.SentCount, '%'),
                deltaUnit: '%' as const,
                higherIsBetter: true,
                trend: trend.map((m) => m.SentCount),
                color: 'eventSent' as const,
                format: (v: number) => formatNumber(v),
            },
            {
                label: 'Delivery rate',
                value: formatPercent(latest.ReceivedPercentage),
                delta: compare((m) => m.ReceivedPercentage, 'pp'),
                deltaUnit: 'pp' as const,
                higherIsBetter: true,
                trend: trend.map((m) => m.ReceivedPercentage),
                color: 'eventReceived' as const,
                format: (v: number) => formatPercent(v),
            },
            {
                label: 'Open rate',
                value: formatPercent(latest.OpenPercentage),
                delta: compare((m) => m.OpenPercentage, 'pp'),
                deltaUnit: 'pp' as const,
                higherIsBetter: true,
                trend: trend.map((m) => m.OpenPercentage),
                color: 'eventOpened' as const,
                format: (v: number) => formatPercent(v),
            },
            {
                label: 'Click rate',
                value: formatPercent(latest.ClickPercentage),
                delta: compare((m) => m.ClickPercentage, 'pp'),
                deltaUnit: 'pp' as const,
                higherIsBetter: true,
                trend: trend.map((m) => m.ClickPercentage),
                color: 'eventClicked' as const,
                format: (v: number) => formatPercent(v),
            },
            {
                label: 'Unsubscribe rate',
                value: formatPercent(latest.UnsubscribePercentage, 2),
                delta: compare((m) => m.UnsubscribePercentage, 'pp'),
                deltaUnit: 'pp' as const,
                higherIsBetter: false,
                trend: trend.map((m) => m.UnsubscribePercentage),
                color: 'eventUnsubscribed' as const,
                format: (v: number) => formatPercent(v, 2),
            },
            {
                label: 'Bounce rate',
                value: formatPercent(latest.BouncePercentage, 2),
                delta: compare((m) => m.BouncePercentage, 'pp'),
                deltaUnit: 'pp' as const,
                higherIsBetter: false,
                trend: trend.map((m) => m.BouncePercentage),
                color: 'eventBounced' as const,
                format: (v: number) => formatPercent(v, 2),
            },
            {
                label: 'Inactive rate',
                value: formatPercent(latest.InactivePercentage),
                delta: compare((m) => m.InactivePercentage, 'pp'),
                deltaUnit: 'pp' as const,
                higherIsBetter: false,
                trend: trend.map((m) => m.InactivePercentage),
                color: 'eventInactive' as const,
                format: (v: number) => formatPercent(v),
            },
        ];
    });

    const topLinks = computed<InsightsBarListItem[]>(() =>
        [...(data.value?.TopLinks ?? [])]
            .sort((a, b) => b.ClickCount - a.ClickCount)
            .map((l) => ({
                key: l.Url,
                label: l.Url.replace(/^https?:\/\//, ''),
                value: l.ClickCount,
                displayValue: formatNumber(l.ClickCount),
                href: l.Url,
            }))
    );

    const geoBy = ref<'country' | 'city'>('country');
    const geoByOptions = [
        { name: 'Countries', value: 'country' },
        { name: 'Cities', value: 'city' },
    ];
    // Same as the issue statistics: with a single country only the city breakdown is meaningful
    const singleCountry = computed(() => (data.value?.GeoIp.GeoIpCountryStats.length ?? 0) === 1);
    const showByCity = computed(() => geoBy.value === 'city' || singleCountry.value);

    const locations = computed<InsightsBarListItem[]>(() => {
        const geoIp = data.value?.GeoIp;
        if (!geoIp) return [];
        return showByCity.value
            ? [...geoIp.GeoIpCityStats]
                  .sort((a, b) => b.OpenCount - a.OpenCount)
                  .map((c) => ({
                      key: `${c.CountryCode}-${c.CityName}`,
                      label: c.CityName,
                      flag: c.CountryCode,
                      value: c.OpenCount,
                      displayValue: formatPercent(c.OpenPercentage),
                  }))
            : [...geoIp.GeoIpCountryStats]
                  .sort((a, b) => b.OpenCount - a.OpenCount)
                  .map((c) => ({
                      key: c.CountryCode,
                      label: c.CountryName,
                      flag: c.CountryCode,
                      value: c.OpenCount,
                      displayValue: formatPercent(c.OpenPercentage),
                  }));
    });

    const mailClients = computed<InsightsBarListItem[]>(
        () =>
            data.value?.MailClients.UserAgents.map((u) => ({
                key: u.Name,
                label: u.Name,
                value: u.OpenCount,
                displayValue: formatPercent(u.OpenPercentage),
            })) ?? []
    );

    const UNSUBSCRIBE_REASON_LABELS: Record<number, string> = {
        1: 'No longer want these emails',
        2: 'Too frequent',
        3: 'Never signed up',
        4: 'Inappropriate content',
        5: 'Spam',
        6: 'Other',
    };

    const unsubscribeReasons = computed<InsightsBarListItem[]>(() => {
        const reasons = data.value?.UnsubscribeReasons ?? [];
        const total = reasons.reduce((acc, r) => acc + r.Count, 0) || 1;
        return [...reasons]
            .sort((a, b) => b.Count - a.Count)
            .map((r) => ({
                key: String(r.ReasonValue),
                label: UNSUBSCRIBE_REASON_LABELS[r.ReasonValue] ?? 'Unknown',
                value: r.Count,
                displayValue: `${formatNumber(r.Count)} (${formatPercent((r.Count / total) * 100, 0)})`,
            }));
    });

    const devices = computed(() => {
        const m = data.value?.MailClients;
        return m ? { mobile: m.MobilePercentage, desktop: m.DesktopPercentage } : { mobile: 0, desktop: 0 };
    });
</script>

<template>
    <div class="insights-reports container-fluid p-lg-t p-lg-b">
        <header>
            <p class="fw-600">
                <span class="glyphicon glyphicon-chevron-left m-xs-r"></span>
                <router-link :to="{ name: 'InsightsReports' }" class="hidden-print">{{ $Resources.BackButton }}</router-link>
            </p>
            <h1 class="m-0-t m-xs-b">Email report</h1>
            <p class="m-md-b">{{ scopeDescription }}</p>
        </header>

        <div v-if="subscription" class="insights-filters d-flex flex-wrap align-items-end gap-sm m-md-b p-sticky pos-t-0 p-sm-ver">
            <MultiSelect
                class="flex-1"
                label="Filter by account"
                :items="subscription.Accounts"
                select-prop="AccountId"
                display-prop="Name"
                select-only="AccountId"
                :selected-items="selectedAccountIds"
                @update:selected-items="(ids: string[]) => (selectedAccountIds = ids)" />

            <FormSelect v-model="month" label="Month" :options="monthOptions" />

            <div class="d-flex gap-xs m-auto-l hidden-print">
                <BSDropdownButton menuClass="dropdown-menu-right" :disabled="!data" closeOnClickInside>
                    <template #toggle>
                        <span class="glyphicon glyphicon-export m-xxs-r"></span>
                        {{ $Resources.ExportButton }}
                    </template>
                    <template #content>
                        <li role="presentation" class="dropdown-header">{{ $Resources.ExportToLabel }}:</li>
                        <li>
                            <a href="javascript:void(0)" role="button" @click="exportReport('excel')">{{ $Resources.ExportToExcelButton }}</a>
                        </li>
                        <li>
                            <a href="javascript:void(0)" role="button" @click="exportReport('csv')">{{ $Resources.ExportToCsvButton }}</a>
                        </li>
                    </template>
                </BSDropdownButton>

                <button type="button" class="btn btn-default" :disabled="!data" @click="print">
                    <span class="glyphicon glyphicon-print m-xxs-r"></span>
                    {{ $Resources.PrintButton }}
                </button>
            </div>
        </div>

        <div v-if="data" class="insights-grid-wrap">
            <div class="insights-grid">
                <InsightsAiSummary class="span-12" :report="data" :month-label="monthLabel" />

                <div class="insights-kpis span-12">
                    <InsightsKpiTile
                        v-for="kpi in kpis"
                        :key="kpi.label"
                        :label="kpi.label"
                        :value="kpi.value"
                        :delta="kpi.delta"
                        :delta-unit="kpi.deltaUnit"
                        :higher-is-better="kpi.higherIsBetter"
                        :trend="kpi.trend"
                        :trend-labels="trendLabels"
                        :color="kpi.color"
                        :format-trend-value="kpi.format" />
                </div>

                <InsightsWidget
                    class="span-12 span-6-wide"
                    title="Engagement trend"
                    subtitle="Emails sent with open, click, bounce and unsubscribe rates per month"
                    icon="showChart">
                    <InsightsTrendChart :trend="data.MonthlyTrend" />
                </InsightsWidget>

                <InsightsWidget class="span-12 span-6-wide" title="Daily activity" :subtitle="`Per day in ${monthLabel}, all mailings`" icon="barChart">
                    <InsightsActivityChart :events-over-time="data.EventsOverTime" />
                </InsightsWidget>

                <InsightsWidget class="span-8 row-span-2" title="Recent mailings" subtitle="Latest sent first" icon="mail">
                    <InsightsRecentIssues
                        :issues="data.RecentIssues"
                        :average-open-percentage="data.Totals.OpenPercentage"
                        :show-account="scope.Type === 'subscription' || scope.AccountIds.length > 1" />
                </InsightsWidget>

                <InsightsWidget class="span-4" title="Most engaged contacts" :subtitle="`Opens and clicks in ${monthLabel}`" icon="trophy">
                    <InsightsTopSubscribers :subscribers="data.TopSubscribers.slice(0, 5)" />
                </InsightsWidget>

                <InsightsWidget class="span-4" title="Devices" subtitle="Share of opens" icon="smartphone">
                    <div
                        class="insights-split fw-600"
                        role="img"
                        :aria-label="`Mobile ${formatPercent(devices.mobile)}, desktop ${formatPercent(devices.desktop)}`">
                        <div class="insights-split-bar d-flex gap-xxs">
                            <span
                                :style="{
                                    width: `${devices.mobile}%`,
                                    background: devices.mobile >= devices.desktop ? INSIGHTS_PRIMARY : INSIGHTS_NEUTRAL,
                                }"></span>
                            <span
                                :style="{
                                    width: `${devices.desktop}%`,
                                    background: devices.desktop > devices.mobile ? INSIGHTS_PRIMARY : INSIGHTS_NEUTRAL,
                                }"></span>
                        </div>
                        <div class="d-flex justify-content-between m-sm-t">
                            <span>
                                <InsightsIcon icon="smartphone" :size="18" />
                                {{ formatPercent(devices.mobile, 0) }}
                                mobile
                            </span>
                            <span class="text-right">
                                <InsightsIcon icon="desktop" :size="18" />
                                {{ formatPercent(devices.desktop, 0) }}
                                desktop
                            </span>
                        </div>
                    </div>
                    <h3 class="text-md m-md-t">Mail clients</h3>
                    <InsightsBarList :items="mailClients" :color="INSIGHTS_PRIMARY" />
                </InsightsWidget>

                <InsightsWidget class="span-4" title="Top links" :subtitle="`Total clicks in ${monthLabel}`" icon="link">
                    <InsightsBarList :items="topLinks" :color="INSIGHTS_COLORS.clicked" />
                </InsightsWidget>

                <InsightsWidget class="span-4" :title="showByCity ? 'Opens by city' : 'Opens by country'" subtitle="Share of opens" icon="public">
                    <template v-if="!singleCountry" #actions>
                        <BSToggleButtons v-model:value="geoBy" :options="geoByOptions" button-class="btn-xs" />
                    </template>
                    <InsightsBarList :items="locations" :color="INSIGHTS_COLORS.opened" />
                </InsightsWidget>

                <InsightsWidget
                    class="span-4"
                    title="Unsubscribe reasons"
                    :subtitle="`${formatCompact(data.UnsubscribeReasons.reduce((a, r) => a + r.Count, 0))} responses`"
                    icon="logout">
                    <InsightsBarList :items="unsubscribeReasons" :color="INSIGHTS_COLORS.unsubscribed" />
                </InsightsWidget>
            </div>
        </div>

        <Loading :show="loading" fixed />
    </div>
</template>

<style lang="less">
    .insights-reports {
        --insights-track: var(--brand-secondary-100);
        // Darker than the brand success shade: yellow-green needs a lower lightness to match the red's contrast (~5.5:1)
        --insights-good: hsl(var(--brand-success-hue-sat), 32%);
        --insights-bad: var(--brand-danger);

        min-height: 100%;

        @media print {
            .insights-widget {
                break-inside: avoid;
            }
        }

        // Widget grid
        .insights-grid {
            display: grid;
            grid-template-columns: repeat(12, minmax(0, 1fr));
            gap: var(--ug-spacing-md);
            align-items: stretch;

            > .span-12 {
                grid-column: span 12;
            }
            > .span-4 {
                grid-column: span 4;
            }
            > .span-8 {
                grid-column: span 8;
            }
            > .row-span-2 {
                grid-row: span 2;
            }

            @media (max-width: 1199px) {
                > .span-4,
                > .span-8 {
                    grid-column: span 12;
                }
                > .row-span-2 {
                    grid-row: auto;
                }
            }

            // Wide screens: the two charts side by side
            @container insights-grid (min-width: 1200px) {
                > .span-6-wide {
                    grid-column: span 6;
                }
            }
        }

        // Sticky filter bar
        .insights-filters {
            z-index: 3;
            background-color: var(--bg-color);
            border-bottom: 1px solid var(--border-color);
        }

        .insights-grid-wrap {
            container: insights-grid / inline-size;
        }

        .insights-kpis {
            display: grid;
            // Sized by available width (not viewport, which includes the nav): max 4 columns, tiles never narrower than 200px
            grid-template-columns: repeat(auto-fill, minmax(max(200px, calc((100% - 3 * var(--ug-spacing-md)) / 4)), 1fr));
            gap: var(--ug-spacing-md);

            // Wide screens: all KPIs in one row
            @container insights-grid (min-width: 1600px) {
                grid-template-columns: repeat(8, minmax(0, 1fr));
            }
        }

        .insights-icon {
            vertical-align: -0.2em;
        }

        // Widget shell
        .insights-widget {
            min-width: 0;
            background: var(--bg-color);
            border-radius: var(--border-radius-large);
            padding: var(--ug-spacing-md);
            box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);
            border: 1px solid var(--border-color);
        }

        .insights-widget-icon {
            width: 32px;
            height: 32px;
            border-radius: var(--border-radius);
            background: var(--brand-tertiary-500);
            box-shadow:
                rgba(50, 50, 93, 0.25) 0px 2px 5px -1px,
                rgba(0, 0, 0, 0.3) 0px 1px 3px -1px;
        }

        .insights-widget-body {
            min-width: 0;
        }

        .insights-chart {
            height: 260px;
        }

        // KPI tile
        .insights-kpi-value {
            font-size: var(--font-size-h2);
            line-height: 1.2;
            font-variant-numeric: tabular-nums;
        }

        .insights-kpi-delta {
            padding: var(--ug-spacing-xxs) var(--ug-spacing-xs);
            border-radius: var(--border-radius);
            background: var(--insights-track);

            &.is-good {
                color: #fff;
                background: var(--insights-good);
            }

            &.is-bad {
                color: #fff;
                background: var(--insights-bad);
            }
        }

        .insights-kpi-trend {
            height: 40px;

            canvas {
                cursor: crosshair;
            }
        }

        .insights-kpi-footnote {
            min-height: 18px;
        }

        // Tracks & bars
        .insights-track {
            height: 8px;
            background: var(--insights-track);
            overflow: hidden;
            box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.15) inset;
        }

        .insights-track-thin {
            height: 6px;
        }

        .insights-account-name {
            max-width: 10rem;
        }

        // Takes the remaining width and lets its content ellipsis instead of growing the column
        .insights-mailing-cell {
            width: 100%;
            max-width: 0;
        }

        .insights-track-fill {
            height: 100%;
            border-radius: inherit;
            transition: width 600ms ease-out;
            // Rows that appear (more items than before) grow in from zero
            animation: insights-track-grow 600ms ease-out;
        }

        @keyframes insights-track-grow {
            from {
                width: 0;
            }
        }

        @media (prefers-reduced-motion) {
            .insights-track-fill {
                transition: none;
                animation: none;
            }
        }

        .insights-barlist-item + .insights-barlist-item {
            margin-top: var(--ug-spacing-md);
        }

        .insights-barlist-label {
            min-width: 0;
        }

        // Device split
        .insights-split-bar {
            height: 12px;

            span:first-child {
                border-radius: var(--border-radius) 0 0 var(--border-radius);
            }

            span:last-child {
                border-radius: 0 var(--border-radius) var(--border-radius) 0;
            }
        }

        // Leaderboard
        .insights-leaderboard-item {
            min-width: 0;

            & + & {
                border-top: 1px solid var(--insights-track);
            }

            a {
                min-width: 0;
            }
        }

        .insights-leaderboard-item + .insights-leaderboard-item {
            padding-top: var(--ug-spacing-md);
        }

        .insights-leaderboard-rank {
            width: 16px;
        }

        // Rows share the list's columns (subgrid) so counts and labels align whatever their width
        .insights-leaderboard {
            grid-template-columns: auto auto minmax(0, 1fr) auto auto;
            column-gap: var(--ug-spacing-sm);

            .insights-leaderboard-item {
                grid-column: 1 / -1;
                grid-template-columns: subgrid;
                align-items: center;
            }
        }

        .insights-leaderboard-stats {
            grid-column: span 2;
            grid-template-columns: subgrid;
            column-gap: 0.25em;
            font-variant-numeric: tabular-nums;
        }

        .insights-avatar {
            width: 32px;
            height: 32px;
        }

        // AI insights
        .insights-ai {
            background-color: var(--brand-secondary-100);
        }

        // Rotating border while the summary is generated: the gradient spins behind an inset layer in the card colour
        .insights-ai-generating {
            position: relative;
            z-index: 0;
            overflow: hidden;

            &::before {
                content: '';
                position: absolute;
                z-index: -2;
                inset: -9999px;
                background-image: conic-gradient(transparent, var(--brand-primary-500) 95%, transparent);
                animation: insights-ai-rotate 4000ms linear infinite;
            }

            &::after {
                content: '';
                position: absolute;
                z-index: -1;
                inset: 2px;
                border-radius: inherit;
                background-color: var(--brand-secondary-100);
            }

            > * {
                opacity: 0.75;
            }
        }

        @keyframes insights-ai-rotate {
            to {
                transform: rotate(1turn);
            }
        }

        @media (prefers-reduced-motion) {
            .insights-ai-generating::before {
                animation: none;
            }
        }

        .insights-ai-list {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: var(--ug-spacing-md);
        }

        .insights-ai-item {
            padding: var(--ug-spacing-sm);
            border-radius: var(--border-radius);
            background: var(--bg-color);
            border: 1px solid var(--border-color);
        }

        .insights-ai-item-icon {
            width: 28px;
            height: 28px;
            border-radius: 50%;
            color: #fff;
            background: var(--brand-primary-500);
        }

        .is-positive .insights-ai-item-icon {
            background: var(--insights-good);
        }

        .is-negative .insights-ai-item-icon {
            background: var(--insights-bad);
        }
    }
</style>
